import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowUpRight, Sparkles, ArrowRight, CheckCircle2, CalendarCheck, Clock, Shield } from "lucide-react";
import { Button } from "../components/ui/button";
import { PRICING, SPECIALS, COMMITMENT_PLANS, TRYOUT, FREE_CLASS, CHALLENGE, BLACK_BELT_PATH, FAMILY_PLAN, PRIVATE_LESSONS, WIX, CONTACT } from "../mock";
import Seo from "../components/Seo";

// Button that only links to a verified Wix checkout; otherwise shows "awaiting official link".
const WixButton = ({ url, label, testid, variant = "green" }) => {
  const cls = variant === "green"
    ? "bg-br-green hover:bg-[#00822f] text-white"
    : variant === "yellow" ? "bg-br-yellow hover:bg-[#e6c900] text-[#1A1A1A]" : "bg-[#1A1A1A] hover:bg-black text-white";
  if (!url) {
    return (
      <div data-testid={`${testid}-pending`}>
        <Button disabled className="w-full h-11 rounded-full font-semibold bg-neutral-200 text-neutral-500 disabled:opacity-100">
          <Clock size={15} className="mr-1.5" /> Awaiting official checkout link
        </Button>
        <p className="mt-2 text-xs text-neutral-500 text-center">Reserve by phone/text: <a href={CONTACT.phoneHref} className="text-br-green font-semibold">{CONTACT.phone}</a></p>
      </div>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid={testid}>
      <Button className={`w-full h-auto min-h-[44px] py-2.5 px-4 rounded-full font-semibold text-sm leading-tight whitespace-normal ${cls}`}>{label} <ArrowUpRight size={16} className="ml-1 shrink-0" /></Button>
    </a>
  );
};

const PlanCard = ({ plan, url, label, testid, accent = false }) => (
  <div data-testid={testid} className={`rounded-2xl p-7 border bg-white card-hover flex flex-col ${accent ? "border-br-green ring-2 ring-br-green/20" : "border-neutral-100"}`}>
    {plan.term && <span className="self-start text-xs font-bold text-br-green bg-br-green/10 rounded-full px-3 py-1">{plan.term}</span>}
    <h3 className="mt-3 font-display text-xl font-bold text-[#1A1A1A]">{plan.name}</h3>
    <div className="mt-3 flex items-end gap-1.5">
      <span className="font-display text-4xl font-extrabold text-br-green">{plan.price}</span>
      <span className="mb-1 text-sm text-neutral-500">{plan.period}</span>
    </div>
    <ul className="mt-5 space-y-2.5 flex-1">
      {plan.features.map((f, i) => (
        <li key={i} className="flex items-start gap-2 text-sm text-neutral-700"><Check size={15} className="text-br-green shrink-0 mt-0.5" /> {f}</li>
      ))}
    </ul>
    <div className="mt-7"><WixButton url={url} label={label} testid={`${testid}-btn`} /></div>
  </div>
);

const SectionTitle = ({ id, eyebrow, title, text }) => (
  <div id={id} className="reveal mt-16 scroll-mt-28">
    {eyebrow && <span className="inline-flex items-center gap-1.5 rounded-full bg-br-yellow/20 px-3 py-1 text-xs font-bold text-[#1A1A1A]"><Sparkles size={13} /> {eyebrow}</span>}
    <h2 className={`${eyebrow ? "mt-3" : ""} font-display text-2xl sm:text-3xl font-bold text-[#1A1A1A]`}>{title}</h2>
    {text && <p className="text-neutral-600 mt-2 max-w-3xl">{text}</p>}
  </div>
);

const Pricing = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver((es) => es.forEach((e) => e.isIntersecting && e.target.classList.add("is-visible")), { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="pt-24">
      <Seo
        title="Membership & Pricing — Brazilian Jiu-Jitsu Alameda | Kids, Teens & Adults"
        description="Alameda BJJ pricing: free first class, $99 one-month tryout, $199 six-week Kids Confidence & Discipline Challenge, month-to-month memberships from $155/mo and six-month unlimited plans from $139/mo."
        path="/pricing"
        breadcrumb={[{ name: "Home", path: "/" }, { name: "Pricing", path: "/pricing" }]}
      />
      <section className="bg-[#F4F5F1] py-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <span className="text-sm font-semibold tracking-[0.2em] text-br-green uppercase">Membership & Pricing</span>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold text-[#1A1A1A] max-w-3xl leading-tight">Plans & Prices</h1>
          <p className="mt-5 text-neutral-600 max-w-2xl leading-relaxed">
            Your first class is always <span className="font-semibold text-[#1A1A1A]">free</span>. Then try a month for {TRYOUT.price}, join the six-week kids challenge, or pick a membership. All payments are processed securely through our official booking system at alamedabjj.com.
          </p>
        </div>
      </section>

      <section className="section-pad">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          {/* Starter offers */}
          <div className="grid lg:grid-cols-2 gap-6">
            <div id="free" className="reveal rounded-3xl bg-br-green text-white p-7 sm:p-9 flex flex-col scroll-mt-28" data-testid="pricing-free-class-card">
              <span className="self-start rounded-full bg-white/15 px-3 py-1 text-xs font-bold tracking-wide">START HERE</span>
              <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight">{FREE_CLASS.name} — {FREE_CLASS.price}</h2>
              <ul className="mt-4 space-y-2 flex-1">
                {FREE_CLASS.points.map((p) => <li key={p} className="flex items-center gap-2 text-sm text-green-50"><CheckCircle2 size={16} className="shrink-0" /> {p}</li>)}
              </ul>
              <Link to="/trial" data-testid="pricing-book-free-btn">
                <Button className="mt-6 w-full h-13 py-6 bg-white text-br-green hover:bg-br-yellow hover:text-[#1A1A1A] rounded-full text-base font-extrabold">
                  <CalendarCheck size={18} className="mr-1.5" /> BOOK FREE CLASS
                </Button>
              </Link>
            </div>

            <div id="tryout" className="reveal rounded-3xl bg-[#0E1613] text-white p-7 sm:p-9 relative overflow-hidden flex flex-col scroll-mt-28" data-testid="pricing-tryout-card">
              <div className="absolute top-0 right-0 h-48 w-48 bg-br-yellow/20 rounded-full blur-3xl" />
              <div className="relative flex flex-col flex-1">
                <div className="flex items-start justify-between gap-3">
                  <span className="rounded-full bg-br-yellow text-[#1A1A1A] px-3 py-1 text-xs font-bold tracking-wide">NEW STUDENT OFFER</span>
                  <span className="font-display text-4xl font-extrabold text-br-yellow">{TRYOUT.price}</span>
                </div>
                <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight">{TRYOUT.name}</h2>
                <ul className="mt-4 grid sm:grid-cols-2 gap-2 flex-1">
                  {TRYOUT.points.map((p) => <li key={p} className="flex items-center gap-2 text-neutral-200 text-sm"><CheckCircle2 size={16} className="text-br-green shrink-0" /> {p}</li>)}
                </ul>
                <Link to="/tryout" data-testid="pricing-enroll-99-btn">
                  <Button className="mt-6 w-full h-13 py-6 bg-br-yellow text-[#1A1A1A] hover:bg-[#e6c900] rounded-full text-base font-extrabold">
                    CLAIM {TRYOUT.price} TRYOUT <ArrowRight size={18} className="ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* $199 Kids Challenge */}
          <div id="challenge" className="reveal mt-8 rounded-3xl border-2 border-br-yellow bg-gradient-to-br from-[#FFFBE6] to-white p-7 sm:p-10 scroll-mt-28" data-testid="pricing-challenge-card">
            <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-start">
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-br-yellow px-3 py-1 text-xs font-bold text-[#1A1A1A]"><Shield size={13} /> FOR PARENTS · NEW STUDENTS</span>
                <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold text-[#1A1A1A] leading-tight">The 6-Week {CHALLENGE.name}</h2>
                <p className="mt-2 font-semibold text-br-green text-lg">{CHALLENGE.tagline}</p>
                <p className="text-sm text-neutral-500">{CHALLENGE.by}</p>
                <p className="mt-4 text-neutral-700">{CHALLENGE.summary}</p>
                <ul className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {CHALLENGE.includes.map((i) => <li key={i} className="flex items-center gap-2 text-sm text-neutral-800 bg-white rounded-xl border border-neutral-100 px-3 py-2"><Check size={14} className="text-br-green shrink-0" /> {i}</li>)}
                </ul>
              </div>
              <div className="w-full lg:w-72 bg-white rounded-2xl border border-neutral-100 p-6 text-center shadow-sm">
                <p className="text-xs font-bold text-neutral-500 tracking-wide">SIX WEEKS · FREE UNIFORM</p>
                <div className="mt-1 font-display text-5xl font-extrabold text-br-green">{CHALLENGE.price}</div>
                <div className="mt-5"><WixButton url={CHALLENGE.url} label={`JOIN THE 6-WEEK CHALLENGE — ${CHALLENGE.price}`} testid="challenge-join" variant="yellow" /></div>
                <Link to="/trial" className="mt-3 inline-block text-sm text-br-green font-semibold">or book a free first class</Link>
              </div>
            </div>
          </div>

          {/* Month-to-month */}
          <SectionTitle id="memberships" title="Month-to-Month Memberships" text="No long-term contract. Cancel any time with 30 days' written notice." />
          <div className="mt-7 grid sm:grid-cols-3 gap-6">
            {PRICING.map((plan) => <PlanCard key={plan.id} plan={plan} url={WIX.plans} label="View Plan on Wix" testid={`plan-${plan.id}`} accent={plan.highlight} />)}
          </div>

          {/* Commitment plans */}
          <SectionTitle id="specials" eyebrow="Web Specials" title="Six-Month & 12-Month Unlimited Memberships"
            text="Our lowest monthly rates for unlimited classes. A six-month membership means you commit to six consecutive monthly payments (the 12-month plan to twelve) in exchange for the discounted rate." />
          <div className="mt-7 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {COMMITMENT_PLANS.map((plan) => <PlanCard key={plan.id} plan={plan} url={plan.url} label="Enroll on Wix" testid={`plan-${plan.id}`} />)}
          </div>

          {/* Black Belt Path + Family */}
          <SectionTitle id="more-plans" title="Flexible & Family Plans" />
          <div className="mt-7 grid sm:grid-cols-2 gap-6">
            <PlanCard plan={BLACK_BELT_PATH} url={WIX.plans} label="View Plan on Wix" testid="plan-black-belt-path" />
            <PlanCard plan={FAMILY_PLAN} url={FAMILY_PLAN.url} label="JOIN FAMILY PLAN" testid="plan-family" accent />
          </div>

          {/* Private lessons */}
          <SectionTitle id="private" title="Private Lessons" text="One-on-one coaching with Professor Silva, by appointment." />
          <div className="mt-7 grid sm:grid-cols-2 gap-6" data-testid="private-lessons">
            {[PRIVATE_LESSONS.single, PRIVATE_LESSONS.pack, ...(PRIVATE_LESSONS.promo.active ? [PRIVATE_LESSONS.promo] : [])].map((p) => (
              <div key={p.name} className="rounded-2xl border border-neutral-100 bg-white p-6 card-hover flex items-center justify-between gap-4">
                <div>
                  <h4 className="font-semibold text-[#1A1A1A]">{p.name}</h4>
                  <a href={`${WIX.bookingBase}${PRIVATE_LESSONS.booking}`} target="_blank" rel="noreferrer" className="text-sm text-br-green font-semibold inline-flex items-center gap-1 mt-1">Book a Private Lesson <ArrowUpRight size={14} /></a>
                </div>
                <span className="font-display text-3xl font-extrabold text-br-green shrink-0">{p.price}</span>
              </div>
            ))}
          </div>

          {/* Events */}
          <SectionTitle id="events" title="Events & Other Services" text="Only currently active, confirmed events are listed. Booked and paid securely through our booking system." />
          <div className="mt-7 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SPECIALS.map((s) => (
              <div key={s.id} data-testid={`special-${s.id}`} className={`rounded-2xl border bg-white p-6 card-hover flex flex-col ${s.featured ? "border-br-yellow ring-2 ring-br-yellow/30 sm:col-span-2 lg:col-span-1" : "border-neutral-100"}`}>
                <div className="flex items-start justify-between gap-3">
                  <h4 className="font-semibold text-[#1A1A1A] leading-snug">{s.name}</h4>
                  <span className="font-display text-lg font-extrabold text-br-green shrink-0">{s.price}{s.unit && <span className="text-xs font-semibold text-neutral-500"> {s.unit}</span>}</span>
                </div>
                <p className="text-sm text-neutral-600 mt-2 flex-1">{s.note}</p>
                <div className="mt-4">
                  {s.request ? (
                    <Link to={s.request} data-testid={`special-${s.id}-btn`}>
                      <Button className="w-full h-10 rounded-full bg-[#1A1A1A] hover:bg-black text-white font-semibold text-sm">Request a Party Quote <ArrowRight size={14} className="ml-1" /></Button>
                    </Link>
                  ) : s.url ? (
                    <a href={s.url} target="_blank" rel="noreferrer" data-testid={`special-${s.id}-btn`}>
                      <Button className="w-full h-10 rounded-full bg-br-green hover:bg-[#00822f] text-white font-semibold text-sm">Sign Up on Wix <ArrowUpRight size={14} className="ml-1" /></Button>
                    </a>
                  ) : (
                    <a href={WIX.booking} target="_blank" rel="noreferrer" data-testid={`special-${s.id}-btn`}>
                      <Button variant="outline" className="w-full h-10 rounded-full border-neutral-300 font-semibold text-sm">Book on Wix <ArrowUpRight size={14} className="ml-1" /></Button>
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>

          <p className="mt-12 text-center text-sm text-neutral-500">
            Questions about plans? Call or text <a href={CONTACT.phoneHref} className="text-br-green font-semibold">{CONTACT.phone}</a> or email <a href={`mailto:${CONTACT.email}`} className="text-br-green font-semibold">{CONTACT.email}</a>.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
